"use client";

import * as React from "react";
import {
  Check,
  ChevronsUpDown,
  Database,
  InboxIcon,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { fetchData } from "@/utils/api-handler";

interface DataSourcesListProps {
  onDataSourcesSelect?: (dataSourceIds: string[]) => void;
  /** Current form selection (updates as the user picks). */
  defaultValue?: Array<string | { id?: string }>;
  /**
   * Knowledge bases already saved on the server / last successful save.
   * Cards render only for these — never for unsaved picks.
   */
  connectedIds?: Array<string | { id?: string }>;
  className?: string;
}

interface DataSource {
  created_at?: string;
  updated_at?: string;
  id: string;
  name: string;
  description?: string;
  organization_id?: string;
  dataset_id?: string;
  triver_organization_id?: string;
  groups?: {
    created_at: string;
    updated_at: string;
    id: string;
    name: string;
    description: string;
    group_id: string;
  }[];
}

interface DataSourcesResponse {
  datasources: DataSource[];
  total_count: number;
}

interface ConnectedDataSourceCardProps {
  dataSource: DataSource;
  onRemove: () => void;
}

function normalizeDataSourceIds(
  value?: Array<string | { id?: string }>
): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && typeof item.id === "string") {
        return item.id;
      }
      return "";
    })
    .filter(Boolean);
}

function resolveDataSources(
  ids: string[],
  catalog?: DataSource[]
): DataSource[] {
  return ids.map((id) => {
    const match = catalog?.find((ds) => ds.id === id);
    if (match) return match;
    return {
      id,
      name: "Knowledge base",
      description: "Connected knowledge base",
    };
  });
}

function ConnectedDataSourceCard({
  dataSource,
  onRemove,
}: ConnectedDataSourceCardProps) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm transition-colors hover:border-border">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30">
          <Database className="size-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-foreground">
              {dataSource.name}
            </h4>
            <Badge
              variant="outline"
              className="h-5 rounded-full border-chart-2/30 bg-chart-2/10 px-2 text-[10px] font-medium uppercase tracking-wide text-chart-2"
            >
              Connected
            </Badge>
          </div>
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {dataSource.description?.trim() ||
              "Attached knowledge base for this agent"}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-md border-border/60 text-muted-foreground hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
          onClick={onRemove}
          aria-label={`Disconnect ${dataSource.name}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function DataSourcesList({
  onDataSourcesSelect,
  defaultValue,
  connectedIds = [],
  className,
}: DataSourcesListProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const stableDefaultKey = JSON.stringify(normalizeDataSourceIds(defaultValue));
  const stableConnectedKey = JSON.stringify(normalizeDataSourceIds(connectedIds));

  const stableDefaultValue = React.useMemo(
    () => normalizeDataSourceIds(defaultValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value identity via stableDefaultKey
    [stableDefaultKey]
  );
  const stableConnectedIds = React.useMemo(
    () => normalizeDataSourceIds(connectedIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value identity via stableConnectedKey
    [stableConnectedKey]
  );

  const [selectedDataSources, setSelectedDataSources] =
    React.useState<string[]>(stableDefaultValue);

  const { data: dataSources, isLoading } = useQuery<DataSource[]>({
    queryKey: ["datasources"],
    queryFn: async () => {
      const response: DataSourcesResponse = await fetchData(
        "/api/documents/datasources/all"
      );
      return response.datasources;
    },
  });

  React.useEffect(() => {
    setSelectedDataSources(stableDefaultValue);
  }, [stableDefaultValue]);

  const handleSelect = (dataSourceId: string) => {
    setSelectedDataSources((prev) => {
      const next = prev.includes(dataSourceId)
        ? prev.filter((id) => id !== dataSourceId)
        : [...prev, dataSourceId];
      onDataSourcesSelect?.(next);
      return next;
    });
  };

  const filteredDataSources = dataSources?.filter((dataSource) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      dataSource.name.toLowerCase().includes(query) ||
      (dataSource.description || "").toLowerCase().includes(query)
    );
  });

  const selectedItems = resolveDataSources(selectedDataSources, dataSources);
  // Cards only for IDs that are saved AND still selected.
  const cardIds = stableConnectedIds.filter((id) =>
    selectedDataSources.includes(id)
  );
  const connectedItems = resolveDataSources(cardIds, dataSources);

  const triggerLabel =
    selectedDataSources.length === 0
      ? "Select knowledge bases"
      : selectedDataSources.length === 1
        ? selectedItems[0]?.name || "1 knowledge base selected"
        : `${selectedDataSources.length} knowledge bases selected`;

  return (
    <div className="space-y-4">
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-10 w-full justify-between border-border/60 bg-background font-normal",
              className
            )}
            disabled={isLoading}
          >
            <span className="flex min-w-0 items-center gap-2">
              <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{triggerLabel}</span>
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="flex w-[var(--radix-popover-trigger-width)] max-h-[min(320px,70vh)] flex-col overflow-hidden p-0"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <div className="shrink-0 border-b p-2">
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">
                <Search className="h-4 w-4" aria-hidden />
              </span>
              <Input
                placeholder="Search knowledge bases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 border-border/60 bg-muted/30 pl-9 focus-visible:ring-1"
                autoComplete="off"
              />
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
            onWheel={(event) => event.stopPropagation()}
          >
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Loading knowledge bases...
                </p>
              </div>
            ) : !filteredDataSources || filteredDataSources.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <InboxIcon className="h-7 w-7 text-muted-foreground/70" />
                <p className="text-xs text-muted-foreground">
                  No knowledge bases found
                </p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredDataSources.map((dataSource) => {
                  const selected = selectedDataSources.includes(dataSource.id);
                  return (
                    <button
                      key={dataSource.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelect(dataSource.id);
                      }}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors",
                        selected
                          ? "bg-primary/10 text-foreground"
                          : "hover:bg-muted/70"
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                          selected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background"
                        )}
                      >
                        {selected ? <Check className="h-3 w-3" /> : null}
                      </span>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                        <Database className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {dataSource.name}
                        </span>
                        {dataSource.description?.trim() ? (
                          <span className="block truncate text-xs text-muted-foreground">
                            {dataSource.description}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {connectedItems.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-muted-foreground">
              Connected knowledge bases
            </p>
            <span className="text-xs text-muted-foreground">
              {connectedItems.length} connected
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {connectedItems.map((dataSource) => (
              <ConnectedDataSourceCard
                key={dataSource.id}
                dataSource={dataSource}
                onRemove={() => handleSelect(dataSource.id)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
